// src/features/auth/pages/LoginPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { COLOR, FW, BORDER } from "@/ui/tokens";
import { SPC_MODEL, getDeviceModel } from "@/lib/androidBridge";

const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

const withBase = (p: string) => (import.meta.env.BASE_URL || "./") + p.replace(/^\/+/, "");

function mapLoginError(r: Response, body: any) {
    const code = String(body?.error || body?.code || "");
    if (code === "INVALID_CREDENTIALS") return "아이디 또는 비밀번호가 올바르지 않습니다.";
    if (code === "USER_DISABLED") return "계정이 비활성화되었습니다. 고객센터에 문의해주세요.";
    if (code === "TOO_MANY_ATTEMPTS") return "시도가 너무 많습니다. 잠시 후 다시 시도해주세요.";
    if (r.status === 401) return "아이디 또는 비밀번호가 올바르지 않습니다.";
    if (r.status === 429) return "시도가 너무 많습니다. 잠시 후 다시 시도해주세요.";
    if (r.status >= 500) return "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
    return body?.message || "로그인에 실패했습니다.";
}

// 디자인 고정값
const BASE = {
    W: 1264,

    TOP_PAD: 312,
    LPAD: 270,

    HEAD_FZ: 64,
    HEAD_ICON: 96,

    INPUT_W: 724,
    INPUT_H: 124,
    INPUT_FZ: 35,

    BTN_W: 724,
    BTN_H: 124,
    BTN_FZ: 40,

    GAP: 24,
    HEAD_TO_INPUT_GAP: 77,
    LINKS_GAP_TOP: 75,
} as const;

function useScale() {
    const { s, px, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    const spx = px;
    return { s, px, spx, sfpx };
}

const ICONS = {
    brand: withBase("icons/ReadersNote_logo.svg"),
    showPw: withBase("icons/show_pw.svg"),
    hidePw: withBase("icons/hide_pw.svg"),
};

export default function LoginPage() {
    const { s, spx, sfpx } = useScale();
    const nav = useNavigate();

    const [username, setUsername] = useState("");
    const loc = useLocation() as any;
    const [pw, setPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const canSubmit = useMemo(() => !!username.trim() && !!pw, [username, pw]);
    const isSpc = useMemo(() => getDeviceModel() === SPC_MODEL, []);

    useEffect(() => {
        const prefill = String(loc.state?.prefill_username || "");
        if (!prefill) return;
        setUsername(prefill);
        setPw("");
        nav("/auth/login", { replace: true, state: null });
    }, [loc.state, nav]);

    const onSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit || loading) return;
        setLoading(true); setErr(null);
        try {
            const r = await fetch(`${AUTH_BASE}/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ identifier: username.trim(), password: pw, app_id: "booktalk" }),
            });
            const ctype = r.headers.get("content-type") || "";
            const isJson = ctype.includes("application/json");
            const data = isJson ? await r.json().catch(() => ({})) : { message: await r.text().catch(() => "") };
            if (!r.ok) {
                setErr(mapLoginError(r, data));
                return;
            }
            localStorage.setItem("bt.refresh", data.refresh_token);
            localStorage.setItem("bt.access", data.access_token);
            localStorage.setItem("bt.user", JSON.stringify(data.user));

            const bridge = (window as any).Android;
            if (typeof bridge?.saveAuthTokenJson === "function") {
                bridge.saveAuthTokenJson(JSON.stringify({
                    access_token: data.access_token,
                    refresh_token: data.refresh_token,
                    user: data.user,
                }));
            }

            // 이 서비스(app_id)의 필수 약관 동의가 없으면 서비스 화면 대신 동의 화면으로.
            // 토큰은 이미 저장됐으므로 동의 API 호출에 그대로 쓴다.
            if (data.consent_required) {
                nav("/auth/agree", {
                    replace: true,
                    state: {
                        mode: "consent",
                        appId: data.consent_app_id || "booktalk",
                        missing: data.consent_missing || [],
                    },
                });
                return;
            }

            const from = loc.state?.from || new URLSearchParams(window.location.search).get("next") || "/search";
            nav(from, { replace: true });
        } catch (e: any) {
            setErr(e?.message || "로그인에 실패했습니다.");
        } finally {
            setLoading(false);
        }
    }, [username, pw, canSubmit, loading, nav]);

    // Shared form fields (inputs + button) — width controlled by parent
    const formFields = (
        <>
            <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="아이디 입력"
                inputMode="text"
                autoComplete="username"
                style={{
                    width: "100%",
                    height: spx(BASE.INPUT_H),
                    borderRadius: s(12),
                    padding: `0 ${spx(16)}`,
                    border: BORDER.boldDark,
                    fontSize: sfpx(BASE.INPUT_FZ),
                    fontWeight: FW.extraBold,
                    boxSizing: "border-box",
                }}
            />
            <div style={{ height: spx(BASE.GAP) }} />
            <div style={{ position: "relative" }}>
                <input
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="비밀번호 입력"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    style={{
                        width: "100%",
                        height: spx(BASE.INPUT_H),
                        borderRadius: s(12),
                        padding: `0 ${spx(72)} 0 ${spx(16)}`,
                        border: "1.5px solid #000",
                        fontSize: sfpx(BASE.INPUT_FZ),
                        fontWeight: 800,
                        boxSizing: "border-box",
                    }}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw(v => !v)}
                    style={{
                        position: "absolute",
                        right: spx(16),
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        lineHeight: 0,
                    }}
                >
                    <img src={showPw ? ICONS.hidePw : ICONS.showPw} alt={showPw ? "숨기기" : "보기"} style={{ width: spx(48), height: spx(48), display: "block" }} />
                </button>
            </div>

            {err && (
                <>
                    <div style={{ height: spx(12) }} />
                    <div role="alert" aria-live="polite" style={{ color: COLOR.error, fontSize: sfpx(18) }}>{err}</div>
                </>
            )}

            <div style={{ height: spx(BASE.GAP) }} />
            <button
                type="submit"
                disabled={!canSubmit || loading}
                style={{
                    width: "100%",
                    height: spx(BASE.BTN_H),
                    borderRadius: s(12),
                    background: COLOR.black,
                    color: COLOR.white,
                    fontSize: sfpx(BASE.BTN_FZ),
                    fontWeight: FW.extraBold,
                    opacity: (!canSubmit || loading) ? 0.6 : 1,
                    border: "none",
                }}
            >
                {loading ? "로그인 중…" : "로그인"}
            </button>
        </>
    );

    return (
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "start center", width: "100%" }}>
            <div style={{ width: spx(BASE.W), background: COLOR.white, paddingTop: spx(BASE.TOP_PAD) }}>

                {isSpc ? (
                    /*
                     * SPC: inline-flex column so the subtitle text sets the container width,
                     * and the form stretches to match. paddingRight adds the +10px.
                     */
                    <div style={{ margin: "0 auto", width: "fit-content", paddingRight: 10 }}>
                        <div style={{ fontWeight: FW.extraBold, fontSize: sfpx(BASE.HEAD_FZ), lineHeight: 1.15, whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: spx(12) }}>
                                <span>독서노트</span>
                                <img src={ICONS.brand} alt="" style={{ width: spx(BASE.HEAD_ICON), height: spx(BASE.HEAD_ICON) }} />
                            </div>
                            <div>책과 함께 깊어지는 나의 하루</div>
                        </div>
                        <form onSubmit={onSubmit} style={{ marginTop: spx(BASE.HEAD_TO_INPUT_GAP), width: "100%" }}>
                            {formFields}
                        </form>
                    </div>
                ) : (
                    <>
                        <div style={{ marginLeft: spx(BASE.LPAD), display: "flex", alignItems: "flex-end", gap: spx(16) }}>
                            <div style={{ fontWeight: FW.extraBold, fontSize: sfpx(BASE.HEAD_FZ), lineHeight: 1.15 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: spx(12) }}>
                                    <span>독서노트</span>
                                    <img src={ICONS.brand} alt="" style={{ width: spx(BASE.HEAD_ICON), height: spx(BASE.HEAD_ICON) }} />
                                </div>
                                <div>책과 함께 깊어지는 나의 하루</div>
                            </div>
                        </div>
                        <form
                            onSubmit={onSubmit}
                            style={{
                                marginLeft: spx(BASE.LPAD),
                                marginTop: spx(BASE.HEAD_TO_INPUT_GAP),
                                width: spx(BASE.INPUT_W),
                            }}
                        >
                            {formFields}
                        </form>
                    </>
                )}

                {/* 버튼 ↔ 링크 간격 넉넉히 + 링크는 화면 '정가운데', 검은색 볼드 */}
                <div style={{ height: spx(BASE.LINKS_GAP_TOP) }} />
                <div style={{ textAlign: "center", fontSize: sfpx(32), fontWeight: FW.extraBold, color: COLOR.black }}>
                    <a href="https://auth.innoauth.com/#/auth/find-account?return_url=https%3A%2F%2Finnobooktox.com%2F%23%2Fauth%2Flogin" style={{ textDecoration: "none", color: COLOR.black, fontWeight: FW.extraBold }}>
                        아이디/비밀번호 찾기
                    </a>
                    <span aria-hidden style={{ margin: `0 ${spx(16)}`, display: "inline-block", width: 2, height: sfpx(32), background: COLOR.black, verticalAlign: "middle" }} />
                    {/* mode:'signup' 을 명시해야 이전에 남은 "로그인 후 동의" 마커가 정리된다 */}
                    <Link to="/auth/agree" state={{ mode: "signup" }} style={{ textDecoration: "none", color: COLOR.black, fontWeight: FW.extraBold }}>
                        회원가입
                    </Link>
                </div>
            </div>
        </div>
    );
}
